import io from "socket.io-client";
import { SOCKET_URL } from "../config/apiConfig";

// Use centralized socket URL configuration

// List of all available socket events - centralized for consistency
const EVENTS = {
  // Connection events
  JOIN_DRIVER_ROOM: "joinDriverRoom",
  JOIN_USER_ROOM: "joinUserRoom",
  JOIN_ADMIN_ROOM: "joinAdminRoom",
  JOIN_VENDOR_ROOM: "joinVendorRoom",

  // Wallet and commission events
  WALLET_UPDATED: "walletUpdated",
  COMMISSION_PROCESSED: "commissionProcessed",
  WITHDRAWAL_UPDATED: "withdrawalUpdated",

  // Booking lifecycle events
  DRIVER_ASSIGNED: "driverAssigned",
  BOOKING_UPDATED: "bookingUpdated",
  BOOKING_STATUS_CHANGED: "bookingStatusChanged",
  RIDE_CANCELLED: "rideCancelled",
  RIDE_STARTED: "rideStarted",
  RIDE_COMPLETED: "rideCompleted",

  // Payment and refund events
  PAYMENT_RECEIVED: "paymentReceived",
  REFUND_INITIATED: "refundInitiated",
  REFUND_PROCESSED: "refundProcessed",
  REFUND_FAILED: "refundFailed",

  // Rating events
  RATING_REQUESTED: "ratingRequested",
  RATING_SUBMITTED: "ratingSubmitted",
  DRIVER_RATING_UPDATE: "driverRatingUpdate",

  // Driver location events
  DRIVER_LOCATION_UPDATED: "driverLocationUpdated",
};

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.events = EVENTS;
    this._disabled = false;
  }

  // Initialize the socket connection
  connect() {
    if (this._disabled) return null;

    // Debug socket configuration

    // Prevent attempts from unsupported origins (remote server CORS)
    const allowedOrigins = new Set([
      'http://localhost:3000',
      'https://urbanride-app.vercel.app',
      'https://urbanride.rajeshwarinfotech.com'
    ]);
    const currentOrigin = window.location.origin;
    if (!allowedOrigins.has(currentOrigin) && SOCKET_URL.includes('rajeshwarinfotech.com')) {
      console.warn(`SocketService: origin ${currentOrigin} is not in the allowlist but will attempt connection.`);
      // Do not disable the socket client here; allow server/proxy to accept or reject.
    }
    // If already connected, return existing socket
    if (this.socket && this.isConnected) {
      return this.socket;
    }

    // If socket exists but is disconnected, clean it up first
    if (this.socket && !this.isConnected) {
      this.socket.removeAllListeners();
      this.socket = null;
    }

    // Implement connection debouncing - only allow one connection attempt per second
    const now = Date.now();
    if (
      this._lastConnectionAttempt &&
      now - this._lastConnectionAttempt < 1000
    ) {
      this._isConnecting = false;
      return null;
    }
    this._lastConnectionAttempt = now;

    // Create new socket instance with improved configuration

    this.socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ["polling", "websocket"], // start with polling, then upgrade to websocket
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 5,
      timeout: 15000,
      upgrade: true, // Allow upgrade to websocket
      forceNew: false,
      withCredentials: false,
    });

    // Connection successful
    this.socket.on("connect", () => {
      this.isConnected = true;
      this._isConnecting = false;
      this._connectionAttempts = 0;
      this._retryCount = 0;
      this._hasWarnedDisconnected = false;
      try {
        const transport = this.socket && this.socket.io && this.socket.io.engine && this.socket.io.engine.transport
          ? this.socket.io.engine.transport.name
          : 'unknown';
        console.log(`SocketService: connected to ${SOCKET_URL} (id=${this.socket.id}, transport=${transport})`);
      } catch (e) {
        console.log('SocketService: connected (transport unknown)');
      }
    });

    // Handle disconnection
    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this._hasWarnedDisconnected = false;
      console.warn(`SocketService: disconnected (reason=${reason})`);
    });

    // Handle connection errors
    this.socket.on("connect_error", (error) => {
      this._connectionAttempts = (this._connectionAttempts || 0) + 1;
      console.error(`SocketService: connect_error (attempt ${this._connectionAttempts}):`, error instanceof Error ? error : error);
      this.isConnected = false;
      this._isConnecting = false;

      if (this._connectionAttempts >= 3) {
        this._disabled = true;
        try { this.socket.removeAllListeners(); } catch (_) {}
        try { this.socket.close(); } catch (_) {}
        this.socket = null;
        console.warn('SocketService: disabled after repeated connection failures');
      }
    });

    // Additional helpful debug events
    this.socket.on('connect_timeout', (timeout) => {
      console.warn('SocketService: connect_timeout', timeout);
    });

    this.socket.on('reconnect_attempt', (attempt) => {
      console.log('SocketService: reconnect_attempt', attempt);
    });

    this.socket.on('reconnect', (attempt) => {
      console.log('SocketService: reconnect successful on attempt', attempt);
    });

    this.socket.on('reconnect_error', (err) => {
      console.error('SocketService: reconnect_error', err);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('SocketService: reconnect_failed');
    });

    this.socket.on('ping', () => {
      // keep light log to avoid noise
      // console.debug('SocketService: ping');
    });

    this.socket.on('pong', (latency) => {
      console.log('SocketService: pong (latency_ms)=', latency);
    });

    return this.socket;
  }

  // Join a room as a driver
  joinDriverRoom(driverId) {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinDriverRoom called but socket is not connected', { driverId });
      return;
    }

    console.log('SocketService: emitting joinDriverRoom', { driverId });
    this.socket.emit(EVENTS.JOIN_DRIVER_ROOM, driverId);
  }

  // Join a room as a user
  joinUserRoom(userId) {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinUserRoom called but socket is not connected', { userId });
      return;
    }

    console.log('SocketService: emitting joinUserRoom', { userId });
    this.socket.emit(EVENTS.JOIN_USER_ROOM, userId);
  }

  // Join the admin room
  joinAdminRoom() {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinAdminRoom called but socket is not connected');
      return;
    }

    console.log('SocketService: emitting joinAdminRoom');
    this.socket.emit(EVENTS.JOIN_ADMIN_ROOM);
  }

  // Join a room as a vendor
  joinVendorRoom(vendorId) {
    if (!this.socket || !this.isConnected) {
      console.warn('SocketService: joinVendorRoom called but socket is not connected', { vendorId });
      return;
    }

    console.log('SocketService: emitting joinVendorRoom', { vendorId });
    this.socket.emit(EVENTS.JOIN_VENDOR_ROOM, vendorId);
  }

  /**
   * Generic method to listen for any socket event
   * @param {string} eventName - Event name from EVENTS object
   * @param {Function} callback - Callback function to handle the event
   */
  listenForEvent(eventName, callback) {
    if (this._disabled) {
      // Silently ignore when sockets are disabled
      return () => {};
    }

    if (!this.socket || !this.isConnected) {
      // Reduce console spam - only warn on first attempt
      if (!this._hasWarnedDisconnected) {
        this._hasWarnedDisconnected = true;
      }

      // Try to connect if socket doesn't exist
      if (!this.socket && !this._isConnecting) {
        this._isConnecting = true;
        this.connect();
      }

      // If still not connected, defer the listener registration with exponential backoff
      if (!this.isConnected) {
        const retryDelay = Math.min(1000 * Math.pow(2, (this._retryCount || 0)), 30000);
        this._retryCount = (this._retryCount || 0) + 1;

        // Only retry a reasonable number of times
        if (this._retryCount < 6) {
          setTimeout(() => this.listenForEvent(eventName, callback), retryDelay);
        }
        return () => {};
      }
    }

    if (!Object.values(EVENTS).includes(eventName)) {
      console.error(`Unknown event: ${eventName}`);
      return;
    }

    // Remove any existing listeners for this event to prevent duplicates
    this.socket.off(eventName);

    // Add the new listener
    this.socket.on(eventName, (data) => {
      callback(data);
    });

    return () => {
      // Provide a cleanup function that can be called to remove this listener
      if (this.socket) {
        this.socket.off(eventName);
      }
    };
  }

  /**
   * Subscribe to all booking updates (status changes, assignments, etc.)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForBookingUpdates(callback) {
    this.listenForEvent(EVENTS.BOOKING_UPDATED, callback);
    this.listenForEvent(EVENTS.BOOKING_STATUS_CHANGED, callback);
  }

  /**
   * Subscribe to driver assignment events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForDriverAssignment(callback) {
    this.listenForEvent(EVENTS.DRIVER_ASSIGNED, callback);
  }

  /**
   * Subscribe to ride cancellation events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRideCancellation(callback) {
    this.listenForEvent(EVENTS.RIDE_CANCELLED, callback);
  }

  /**
   * Subscribe to payment and refund events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForPaymentUpdates(callback) {
    this.listenForEvent(EVENTS.PAYMENT_RECEIVED, callback);
    this.listenForEvent(EVENTS.REFUND_INITIATED, callback);
    this.listenForEvent(EVENTS.REFUND_PROCESSED, callback);
    this.listenForEvent(EVENTS.REFUND_FAILED, callback);
  }

  /**
   * Subscribe to ride lifecycle events (started, completed)
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRideLifecycle(callback) {
    this.listenForEvent(EVENTS.RIDE_STARTED, callback);
    this.listenForEvent(EVENTS.RIDE_COMPLETED, callback);
  }

  /**
   * Subscribe to rating events
   * @param {Function} callback - Callback function to handle the event
   */
  listenForRatingEvents(callback) {
    this.listenForEvent(EVENTS.RATING_REQUESTED, callback);
    this.listenForEvent(EVENTS.RATING_SUBMITTED, callback);
  }

  /**
   * Subscribe to driver location updates
   * @param {Function} callback - Callback function to handle the event
   */
  listenForDriverLocationUpdates(callback) {
    this.listenForEvent(EVENTS.DRIVER_LOCATION_UPDATED, callback);
  }

  // Legacy methods for backward compatibility
  onDriverAssigned(callback) {
    this.listenForDriverAssignment(callback);
  }

  onBookingUpdated(callback) {
    this.listenForEvent(EVENTS.BOOKING_UPDATED, callback);
  }

  onBookingStatusChanged(callback) {
    this.listenForEvent(EVENTS.BOOKING_STATUS_CHANGED, callback);
  }

  onRideCancelled(callback) {
    this.listenForRideCancellation(callback);
  }

  onRefundProcessed(callback) {
    this.listenForEvent(EVENTS.REFUND_PROCESSED, callback);
  }

  onRideCompleted(callback) {
    this.listenForEvent(EVENTS.RIDE_COMPLETED, callback);
  }

  onRatingSubmitted(callback) {
    this.listenForEvent(EVENTS.RATING_SUBMITTED, callback);
  }

  onDriverRatingUpdate(callback) {
    this.listenForEvent(EVENTS.DRIVER_RATING_UPDATE, callback);
  }

  // Disconnect the socket safely
  disconnect() {
    if (this.socket) {
      try {
        if (this.socket.connected) {
          console.log('SocketService: disconnecting socket', { id: this.socket.id });
          this.socket.disconnect();
        } else {
          console.log('SocketService: cleaning up listeners for socket (not connected)');
          this.socket.removeAllListeners();
        }
      } catch (error) {
        console.error('SocketService: error during disconnect', error);
      } finally {
        // Always clean up resources regardless of disconnection success
        this.socket = null;
        this.isConnected = false;
        this._isConnecting = false;
        console.log('SocketService: socket cleaned up');
      }
    } else {
      console.log('SocketService: disconnect called but no socket instance exists');
    }
  }
}

// Create a singleton instance
const socketService = new SocketService();

export default socketService;
